import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
// eslint-disable-next-line
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Save, Globe, User, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, token, updateUser } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || ''
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/users/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify({ language })
      });

      if (response.ok) {
        toast.success('Settings saved!');
        updateUser({ language });
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="settings-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            {t('dashboard.settings')}
          </h1>
          <p className="text-slate-600">Manage your account preferences</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="rounded-xl gap-2"
          data-testid="save-settings-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : t('common.save')}
        </Button>
      </motion.div>

      <div className="max-w-2xl space-y-6">
        {/* Account Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 shadow-soft"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-slate-900">Account Information</h2>
              <p className="text-sm text-slate-500">Your account details</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <Label>Username</Label>
              <Input
                value={formData.username}
                disabled
                className="rounded-xl mt-1 bg-slate-50"
                data-testid="username-display"
              />
              <p className="text-xs text-slate-500 mt-1">Username cannot be changed</p>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={formData.email}
                disabled
                className="rounded-xl mt-1 bg-slate-50"
                data-testid="email-display"
              />
            </div>
          </div>
        </motion.div>

        {/* Language */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 shadow-soft"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-slate-900">Language</h2>
              <p className="text-sm text-slate-500">Choose your preferred language</p>
            </div>
          </div>

          <Select value={language} onValueChange={changeLanguage}>
            <SelectTrigger className="rounded-xl" data-testid="language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Security */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 shadow-soft"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-slate-900">Security</h2>
              <p className="text-sm text-slate-500">Manage your account security</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-900">Password</p>
                <p className="text-sm text-slate-500">Last changed: Never</p>
              </div>
              <Button variant="outline" className="rounded-xl" disabled>
                Change
              </Button>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-900">Two-Factor Authentication</p>
                <p className="text-sm text-slate-500">Not enabled</p>
              </div>
              <Button variant="outline" className="rounded-xl" disabled>
                Enable
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Danger Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-red-50 rounded-2xl p-6 border border-red-100"
        >
          <h2 className="font-heading font-semibold text-red-900 mb-2">Danger Zone</h2>
          <p className="text-sm text-red-700 mb-4">
            Once you delete your account, there is no going back. Please be certain.
          </p>
          <Button variant="destructive" className="rounded-xl" disabled>
            Delete Account
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
